import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { formatMoney } from '@/components/billing/formatMoney'
import { formatDateDMY } from '@/utils/date'
import type { BalanceTopupRequest, TopupRequestStatus } from '@/types/tts'

const STATUS_COLORS: Record<TopupRequestStatus, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  confirmed: 'success',
  rejected: 'error',
  cancelled: 'default',
}

interface TopupRequestsListProps {
  requests: BalanceTopupRequest[]
  onCancel?: (request: BalanceTopupRequest) => void
  cancellingId?: number | null
}

export function TopupRequestsList({ requests, onCancel, cancellingId }: TopupRequestsListProps) {
  const { t } = useTranslation()

  if (requests.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('balance.topup.empty', 'Заявок пока нет')}
      </Typography>
    )
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t('balance.topup.createdAt', 'Дата')}</TableCell>
          <TableCell>{t('balance.topup.amount', 'Сумма, ₽')}</TableCell>
          <TableCell>{t('balance.topup.paymentMethod', 'Способ оплаты')}</TableCell>
          <TableCell>{t('balance.topup.status', 'Статус')}</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {requests.map((request) => (
          <TableRow key={request.id}>
            <TableCell>
              {formatDateDMY(request.created_at)}
            </TableCell>
            <TableCell>{formatMoney(request.amount, request.currency)}</TableCell>
            <TableCell>
              {t(`balance.topup.methods.${request.payment_method}`, request.payment_method)}
            </TableCell>
            <TableCell>
              <Tooltip title={request.decision_note ?? ''}>
                <Chip
                  size="small"
                  color={STATUS_COLORS[request.status]}
                  label={t(`balance.topup.statuses.${request.status}`, request.status)}
                />
              </Tooltip>
            </TableCell>
            <TableCell>
              {onCancel && request.status === 'pending' && (
                <Button
                  size="small"
                  color="error"
                  disabled={cancellingId === request.id}
                  onClick={() => onCancel(request)}
                >
                  {t('balance.topup.cancel', 'Отменить')}
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
